import { Link } from "react-router-dom";
import { ShieldCheck, Mail, Phone, MapPin, Zap, Activity, Globe2 } from "lucide-react";
import { SECTORS } from "@/lib/sectors";
import { COMPANY } from "@/lib/company";
import { FOOTER } from "@/constants/testIds";

const LEGAL = [
    { slug: "privacy", label: "Privacy policy" },
    { slug: "terms", label: "Terms of business" },
    { slug: "complaints", label: "Complaints procedure" },
    { slug: "cookies", label: "Cookie policy" },
    { slug: "gdpr", label: "GDPR & data" },
];

export default function Footer() {
    return (
        <footer data-testid={FOOTER.root} className="bg-navy-900 text-slate-300 relative overflow-hidden">
            <div className="absolute inset-0 grid-bg-dark opacity-40 pointer-events-none" />
            <div className="container-x relative py-16 lg:py-20">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
                    <div className="col-span-2 lg:col-span-2">
                        <div className="flex items-center gap-2.5">
                            <div className="relative w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                            </div>
                            <div className="font-display font-bold text-white text-xl">ClaimHub <span className="text-emerald-400">UK</span></div>
                        </div>
                        <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-sm">
                            The UK&apos;s trusted claims hub — covering motor finance, housing disrepair, personal injury and more. Specialist solicitors. No Win, No Fee.
                        </p>
                        <div className="mt-6 space-y-2.5 text-sm">
                            <div className="flex items-center gap-2.5 text-slate-400"><Mail className="w-4 h-4 text-emerald-400" /> {COMPANY.email}</div>
                            <div className="flex items-center gap-2.5 text-slate-400"><Phone className="w-4 h-4 text-emerald-400" /> {COMPANY.phone}</div>
                            <div className="flex items-center gap-2.5 text-slate-400"><MapPin className="w-4 h-4 text-emerald-400" /> {COMPANY.addressShort}</div>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-4">Claim types</div>
                        <ul className="space-y-2.5 text-sm">
                            {SECTORS.slice(0, 5).map((s) => (
                                <li key={s.slug}><Link to={`/sectors/${s.slug}`} className="text-slate-400 hover:text-white transition-colors">{s.name}</Link></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-4">More claims</div>
                        <ul className="space-y-2.5 text-sm">
                            {SECTORS.slice(5).map((s) => (
                                <li key={s.slug}><Link to={`/sectors/${s.slug}`} className="text-slate-400 hover:text-white transition-colors">{s.name}</Link></li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-400 mb-4">Company</div>
                        <ul className="space-y-2.5 text-sm">
                            <li><Link to="/about" className="text-slate-400 hover:text-white">About us</Link></li>
                            <li><Link to="/how-it-works" className="text-slate-400 hover:text-white">How it works</Link></li>
                            <li><Link to="/faq" className="text-slate-400 hover:text-white">FAQ</Link></li>
                            <li><Link to="/contact" className="text-slate-400 hover:text-white">Contact</Link></li>
                            <li><Link to="/admin/login" className="text-slate-500 hover:text-emerald-400">Admin</Link></li>
                        </ul>
                    </div>
                </div>

                {/* Connexis Group — parent company / operating partner */}
                <div className="mt-14 pt-10 border-t border-white/10" data-testid="footer-connexis">
                    <div className="rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm p-6 lg:p-8">
                        <div className="grid lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-7">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-400">
                                        <span className="inline-block w-6 h-px bg-emerald-400" /> Powered by
                                    </div>
                                </div>

                                <div className="mt-3 inline-flex items-center gap-3" data-testid="footer-connexis-lockup">
                                    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-glow-emerald">
                                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-navy-900" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M4 12h6m4 0h6M4 12a4 4 0 1 1 8 0 4 4 0 1 1 8 0" strokeLinecap="round" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="font-display font-bold text-2xl text-white tracking-tight">
                                            Connexis<span className="text-emerald-400"> Group</span>
                                        </div>
                                        <div className="text-xs text-slate-400">Infrastructure for financial growth</div>
                                    </div>
                                </div>

                                <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-2xl">
                                    ClaimHub UK is operated on the Connexis platform — the operating layer behind modern UK &amp; EU financial brands. Real-time enquiry routing, AI voice &amp; chat operations and enterprise-grade compliance infrastructure, delivered as one system.
                                </p>
                            </div>

                            <div className="lg:col-span-5 grid grid-cols-3 gap-3">
                                {[
                                    { icon: Activity, value: "240k+", label: "Enquiries / month" },
                                    { icon: Zap, value: "98.6ms", label: "Median routing" },
                                    { icon: Globe2, value: "99.99%", label: "Uptime SLA" },
                                ].map((s) => (
                                    <div key={s.label} className="rounded-xl bg-navy-900/40 border border-white/10 p-4 text-center">
                                        <s.icon className="w-4 h-4 text-emerald-400 mx-auto" />
                                        <div className="mt-2 font-display font-bold text-lg text-white tracking-tight">{s.value}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5 leading-tight">{s.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-10 pt-8 border-t border-white/10 grid md:grid-cols-2 gap-6 items-center">
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
                        {LEGAL.map((l) => (
                            <Link key={l.slug} to={`/legal/${l.slug}`} data-testid={FOOTER.legalLink(l.slug)} className="hover:text-white transition-colors">{l.label}</Link>
                        ))}
                    </div>
                    <div className="text-xs text-slate-500 md:text-right">
                        © {new Date().getFullYear()} {COMPANY.legalName}. Registered in England &amp; Wales. Operated on the Connexis Group platform.
                        ClaimHub UK is an FCA-aware claims management operation working with SRA-regulated solicitor partners.
                    </div>
                </div>

                <div className="mt-6 text-[11px] text-slate-600 leading-relaxed max-w-4xl">
                    You can complain about a financial product or service yourself directly to the firm for free, or refer it to the Financial Ombudsman Service (FOS) without using a claims management company.
                    Where compensation is recovered, our success fee applies up to FCA capped rates (typically 18 – 30%). Full fee disclosure is provided before any claim is started.
                </div>
            </div>
        </footer>
    );
}
